import { useAtom } from "jotai";
import { useCallback, useRef } from "react";

import type { Story } from "@baiki/interfaces";

import { currentWordIndexAtom, playerAtom } from "@/store/store";

import type { UsePlayerReturn } from "./usePlayer.types";
import { usePlayerActions } from "./usePlayerActions";
import { usePlayerSeeking } from "./usePlayerSeeking";
import { usePlayerSettings } from "./usePlayerSettings";
import { usePlayerSync } from "./usePlayerSync";

/**
 * Comprehensive player hook that manages all audio playback
 * Use this hook to access player state and control playback
 */
export const usePlayer = (): UsePlayerReturn => {
  // ============================================================================
  // State
  // ============================================================================

  const [player, setPlayer] = useAtom(playerAtom);
  const [currentWordIndex, setCurrentWordIndex] = useAtom(currentWordIndexAtom);

  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastSyncedTimeRef = useRef<number>(0);
  const isLoadingRef = useRef<boolean>(false);

  // ============================================================================
  // Compose sub-hooks
  // ============================================================================

  const actions = usePlayerActions({ player, setPlayer });
  const settings = usePlayerSettings({ player, setPlayer });
  const seeking = usePlayerSeeking({
    player,
    setPlayer,
    audioRef,
    lastSyncedTimeRef,
  });

  // Audio synchronization effects
  usePlayerSync({
    player,
    setPlayer,
    audioRef,
    seeking: seeking.seeking,
    currentWordIndex,
    setCurrentWordIndex,
    lastSyncedTimeRef,
    isLoadingRef,
  });

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const isPlayingStory = useCallback(
    (story: Story) => {
      return Boolean(
        story?.id && player.playing && player.story?.id === story.id,
      );
    },
    [player.playing, player.story?.id],
  );

  const isBufferingStory = useCallback(
    (story: Story) => {
      return Boolean(
        story?.id && player.buffering && player.story?.id === story.id,
      );
    },
    [player.buffering, player.story?.id],
  );

  const getElapsedPercent = useCallback(() => {
    return player.duration > 0
      ? (player.currentTime / player.duration) * 100
      : 0;
  }, [player.currentTime, player.duration]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    audioRef,
    currentWordIndex,
    getElapsedPercent,
    isBufferingStory,
    isPlayingStory,
    player,
    ...actions,
    ...settings,
    ...seeking,
  };
};
